/**
 * Centralized icon registry — the SINGLE place allowed to import from the icon
 * library (lucide-react). Maps stable, semantic kebab-case names to a glyph
 * component (a lucide icon, or a custom product glyph from `custom.jsx`).
 *
 * Consumers never import lucide directly; they render <Icon name="settings" />.
 * To add an icon: import the lucide component (or author a custom glyph), then
 * add a `'semantic-name': Glyph` entry below. Prefer the semantic intent
 * ('delete', 'success') over the visual ('trash', 'circle-check') so the glyph
 * can be swapped later without touching call sites.
 *
 * CI guards this: `from 'lucide-react'` is only permitted inside this folder.
 */

import {
  Settings,
  FolderKanban,
  Folder,
  TriangleAlert,
  CircleAlert,
  Info,
  CircleCheck,
  Check,
  X,
  Search,
  Plus,
  Minus,
  Move,
  Trash2,
  Pencil,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Bell,
  Shield,
  ShieldAlert,
  Bug,
  FileText,
  FileSpreadsheet,
  FileJson,
  FileCode,
  FilePen,
  Table,
  BarChart3,
  List,
  ListChecks,
  GripVertical,
  Flag,
  Bookmark,
  RotateCcw,
  Users,
  Star,
  Grid3x3,
  SlidersHorizontal,
  Calculator,
  Sigma,
  History,
  Save,
  Download,
  Upload,
  Image as ImageIcon,
  Filter,
  EllipsisVertical,
  LayoutDashboard,
  Eye,
  EyeOff,
  Lock,
  User,
  LogOut,
  RefreshCw,
  ExternalLink,
  Copy,
  Link2,
  Mail,
  Calendar,
  Clock,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  Menu,
  Home,
  BookOpen,
  CircleHelp,
  Bold,
  Building2,
  Sun,
  Moon,
  LoaderCircle,
  PanelLeft,
  Sparkles,
  Workflow,
  LockOpen,
  Network,
  SendToBack,
  BringToFront,
  ShieldCheck,
  Target,
  Cloud,
  FilePlus,
  Cpu,
  Radio,
  FunctionSquare,
  Frame,
  Undo2,
  Redo2,
  ArrowLeftRight,
  Server,
  Database,
  LayoutGrid,
  CircuitBoard,
  RadioTower,
  Monitor,
  Radar,
  Cog,
  Plug,
  KeyRound,
  Flame,
  AppWindow,
  MemoryStick,
  Zap,
  Wrench,
  Smartphone,
  Car,
  BatteryCharging,
  Skull,
  Box,
  Cable,
  Bluetooth,
  Wifi,
  Usb,
  Nfc,
  Terminal,
  TrendingUp,
} from 'lucide-react';

import {
  ThreatGlyph,
  CalMatrixGlyph,
  TransistorGlyph,
  ResistorGlyph,
  OpAmpGlyph,
  GitHubGlyph,
} from './custom';

export const ICON_REGISTRY = {
  // ── Navigation / chrome ──────────────────────────────────────────────
  settings: Settings,
  sun: Sun,
  moon: Moon,
  dashboard: LayoutDashboard,
  overview: LayoutDashboard,
  project: FolderKanban,
  projects: FolderKanban,
  folder: Folder,
  library: BookOpen,
  home: Home,
  menu: Menu,
  more: EllipsisVertical, // kebab / row-actions trigger

  // ── Status / feedback (pair with tone) ───────────────────────────────
  warning: TriangleAlert,
  error: CircleAlert,
  info: Info,
  success: CircleCheck,
  help: CircleHelp,

  // ── Actions ──────────────────────────────────────────────────────────
  add: Plus,
  minus: Minus,
  move: Move,
  close: X,
  check: Check,
  search: Search,
  edit: Pencil,
  delete: Trash2,
  filter: Filter,
  download: Download,
  upload: Upload,
  image: ImageIcon,
  copy: Copy,
  refresh: RefreshCw,
  'trending-up': TrendingUp,
  link: Link2,
  mail: Mail,
  'external-link': ExternalLink,
  github: GitHubGlyph,

  // ── Direction ─────────────────────────────────────────────────────────
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'arrow-up': ArrowUp,
  'arrow-left-right': ArrowLeftRight,

  // ── Domain / product ──────────────────────────────────────────────────
  notification: Bell,
  building: Building2,
  spinner: LoaderCircle,
  'sidebar-toggle': PanelLeft,
  sparkles: Sparkles,
  diagram: Workflow,
  shield: Shield,
  'shield-alert': ShieldAlert,
  vulnerability: Bug,
  document: FileText,
  'file-spreadsheet': FileSpreadsheet,
  'file-json': FileJson,
  'file-code': FileCode,
  'file-pen': FilePen,
  table: Table,
  chart: BarChart3,
  list: List,
  'list-checks': ListChecks,
  'grip-vertical': GripVertical,
  flag: Flag,
  bookmark: Bookmark,
  'rotate-ccw': RotateCcw,
  users: Users,
  star: Star,
  grid: Grid3x3,
  sliders: SlidersHorizontal,
  calculator: Calculator,
  formula: Sigma,
  history: History,
  save: Save,
  user: User,
  lock: Lock,
  unlock: LockOpen,
  logout: LogOut,
  // ── Diagram canvas controls ──────────────────────────────────────────
  'auto-layout': Network,
  'send-to-back': SendToBack,
  'bring-to-front': BringToFront,
  'shield-check': ShieldCheck,
  target: Target,
  cloud: Cloud,
  server: Server,
  database: Database,
  'file-plus': FilePlus,
  cpu: Cpu,
  signal: Radio,
  'function-square': FunctionSquare,
  frame: Frame,
  undo: Undo2,
  redo: Redo2,
  calendar: Calendar,
  clock: Clock,
  eye: Eye,
  'eye-off': EyeOff,
  bold: Bold,

  // ── Architecture component-node type marks ───────────────────────────
  'layout-grid': LayoutGrid,
  'circuit-board': CircuitBoard,
  network: Network,
  'radio-tower': RadioTower,
  monitor: Monitor,
  radar: Radar,
  cog: Cog,
  plug: Plug,
  bug: Bug,
  'key-round': KeyRound,
  flame: Flame,
  'app-window': AppWindow,
  'memory-stick': MemoryStick,
  zap: Zap,

  // ── Architecture external-entity type marks ──────────────────────────
  wrench: Wrench,
  smartphone: Smartphone,
  car: Car,
  'battery-charging': BatteryCharging,
  skull: Skull,
  box: Box,

  // ── Architecture channel type marks ──────────────────────────────────
  cable: Cable,
  bluetooth: Bluetooth,
  wifi: Wifi,
  usb: Usb,
  nfc: Nfc,
  terminal: Terminal,

  // ── Custom (hybrid) — bespoke product marks, no clean lucide match ────
  threat: ThreatGlyph,
  'cal-matrix': CalMatrixGlyph,
  transistor: TransistorGlyph,
  resistor: ResistorGlyph,
  'op-amp': OpAmpGlyph,
};

/** Fallback glyph rendered when a name is missing from the registry. */
export const DEFAULT_ICON = CircleHelp;

/** All registered semantic names (sorted) — handy for tooling / docs. */
export const ICON_NAMES = Object.keys(ICON_REGISTRY).sort();
